import { useEffect, useRef, useCallback } from 'react';
import { MessageSquare, ListX } from 'lucide-react';
import ChatMessage from './ChatMessage';
import ModelSelector, { AIModel } from './ModelSelector';
import StyleSelector, { VisualStyle } from './StyleSelector';
import UsageIndicator from './UsageIndicator';
import ProcessingPanel from './ProcessingPanel';
import CodeEditProgress from './CodeEditProgress';
import { StreamingChatBubble } from './StreamingChatBubble';
import beeIcon from '../../assets/icons/assistant-bee.png';
import owlIcon from '../../assets/icons/assistant-owl.png';
import squirrelIcon from '../../assets/icons/assistant-squirrel.png';
import { User } from '../../services/userService';

interface UsageData {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  requestCount: number;
  date?: string;
}

interface HistoryEntry {
  type: 'message' | 'code_edit';
  timestamp: string;
  response_id?: string; // UUID for seamless streaming-to-history handoff
  message_details?: {
    role: 'user' | 'assistant' | 'system';
    summary: string;
    model?: 'norm' | 'exp' | 'eco';
    isError?: boolean;
    isWarning?: boolean;  // For abort messages
  };
  code_edit_details?: {
    summary: string;
    complete_summary?: string;
    estimated_lines: number;
    progress: number;
    model?: 'norm' | 'exp' | 'eco';
    isError?: boolean; // Whether validation failed
    isAborted?: boolean; // Whether aborted by user
  };
}


interface ChatHistoryProps {
  history: HistoryEntry[];
  isProcessing: boolean;
  processingUpdateMessage?: string | null;
  selectedModel: AIModel;
  selectedStyle: VisualStyle;
  onModelChange: (model: AIModel) => void;
  onStyleChange: (style: VisualStyle) => void;
  usageData: UsageData | null;
  onClearHistory: () => void;
  processingTime: number;
  currentProcessingMessage: string;
  processingMessageKey: number;
  currentUser: User | null;
  // Live streaming code edit progress props (ephemeral, not from history)
  codeEditSummary?: string | null;
  codeEditCount?: number | null;
  codeEditCompleteSummary?: string | null;
  codeEditLinesReceived?: number;
  codeEditComplete?: boolean;
  isStreaming?: boolean;
  // Live streaming chat messages (ephemeral, not from history)
  streamingChatMessages?: Array<{ 
    id: string;
    timestamp: string;
    content: string;
    sequenceNumber: number;
    isComplete: boolean;
    isWarning?: boolean;  // For abort messages
  }>;
  // Live streaming code edits (ephemeral, not from history)
  streamingCodeEdits?: Array<{
    id: string;
    timestamp: string;
    processingMessage: string;
    completeMessage: string | null;
    estimatedLines: number;
    currentLines: number;
    percentage: number;
    isComplete: boolean;
    isApplied: boolean;
    isError?: boolean; // Whether the code edit failed validation
    isAborted?: boolean; // Whether aborted by user
    sequenceNumber: number;
  }>;
}

const MODEL_INFO = {
  norm: { name: 'Bee', icon: beeIcon },
  exp: { name: 'Owl', icon: owlIcon },
  eco: { name: 'Squirrel', icon: squirrelIcon }
};

// Migration helper: Convert old format to new format
function migrateHistoryEntry(entry: any): HistoryEntry {
  // Already in new format
  if (entry.type) {
    return entry as HistoryEntry;
  }

  // Old format - migrate to new
  if (entry.role) {
    return {
      type: 'message',
      timestamp: entry.timestamp,
      message_details: {
        role: entry.role,
        summary: entry.summary,
        ...(entry.model && { model: entry.model }),
        ...(entry.isError && { isError: entry.isError })
      }
    };
  }

  // Unknown format - return as message type
  console.warn('Unknown history entry format:', entry);
  return {
    type: 'message',
    timestamp: entry.timestamp || new Date().toISOString(),
    message_details: {
      role: 'assistant',
      summary: String(entry.summary || entry),
      isError: true
    }
  };
}

export default function ChatHistory({
  history,
  isProcessing,
  processingUpdateMessage = null,
  selectedModel,
  selectedStyle,
  currentUser,
  onModelChange,
  onStyleChange,
  usageData,
  onClearHistory,
  processingTime,
  currentProcessingMessage,
  processingMessageKey,
  codeEditSummary,
  codeEditCount,
  codeEditCompleteSummary,
  codeEditLinesReceived = 0,
  codeEditComplete = false,
  isStreaming = false,
  streamingChatMessages = [],
  streamingCodeEdits = [],
}: ChatHistoryProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const currentModelInfo = MODEL_INFO[selectedModel];
  
  // Smooth scroll to bottom with lerp animation
  const smoothScrollToBottom = useCallback(() => {
    if (!scrollRef.current) return;
    
    const container = scrollRef.current;
    const targetScroll = container.scrollHeight - container.clientHeight;
    const currentScroll = container.scrollTop;
    const distance = targetScroll - currentScroll;
    
    // If already at bottom or very close, snap immediately
    if (Math.abs(distance) < 1) {
      container.scrollTop = targetScroll;
      return;
    }
    
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Lerp animation with requestAnimationFrame
    const lerpScroll = () => {
      if (!scrollRef.current) return;
      
      const currentPos = scrollRef.current.scrollTop;
      const target = scrollRef.current.scrollHeight - scrollRef.current.clientHeight;
      const diff = target - currentPos;
      
      // Lerp factor: 0.15 = smooth, higher = faster
      const lerpFactor = 0.2;
      const newPos = currentPos + diff * lerpFactor;
      
      // Snap to target if very close
      if (Math.abs(diff) < 0.5) {
        scrollRef.current.scrollTop = target;
        animationFrameRef.current = null;
      } else {
        scrollRef.current.scrollTop = newPos;
        animationFrameRef.current = requestAnimationFrame(lerpScroll);
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(lerpScroll);
  }, []);
  
  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  
  // Migrate history entries on the fly (backwards compatibility)
  const migratedHistory = history.map(migrateHistoryEntry);
  
  // Sort history by timestamp to ensure correct chronological order
  const sortedHistory = [...migratedHistory].sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeA - timeB;
  });
  
  // Get response_ids from history (for filtering out streaming items that are now saved)
  const historyResponseIds = new Set(
    sortedHistory
      .filter(entry => entry.response_id)
      .map(entry => entry.response_id)
  );
  
  // Filter streaming items to exclude those already in history (seamless handoff)
  const visibleStreamingChatMessages = streamingChatMessages.filter(
    msg => !historyResponseIds.has(msg.id)
  );
  const visibleStreamingCodeEdits = streamingCodeEdits.filter(
    edit => !historyResponseIds.has(edit.id)
  );
  
  // Merge and sort streaming items by sequence number for correct display order
  const streamingItems = [
    ...visibleStreamingChatMessages.map(msg => ({ type: 'chat' as const, data: msg, seq: msg.sequenceNumber })),
    ...visibleStreamingCodeEdits.map(edit => ({ type: 'codeEdit' as const, data: edit, seq: edit.sequenceNumber }))
  ].sort((a, b) => a.seq - b.seq);

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    smoothScrollToBottom();
  }, [history, isProcessing, streamingChatMessages, streamingCodeEdits, smoothScrollToBottom]);

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-dark-surface">
      {/* Header - Fixed at top, centered */}
      <div className="flex-shrink-0 flex justify-center bg-gray-50 dark:bg-dark-surface border-b border-gray-200 dark:border-dark-border overflow-visible z-10">
        <div className="w-full max-w-4xl px-4 py-2 pb-3">
          <div className="relative flex items-center justify-center">
            {/* Left: Usage Indicator - Absolute positioned */}
            <div className="absolute left-0">
              <UsageIndicator usage={usageData} />
            </div>
            
            {/* Center: Model & Style Selectors */}
            <div className="flex items-center gap-2">
              <ModelSelector
                selectedModel={selectedModel}
                onModelChange={onModelChange}
              />
              <StyleSelector
                selectedStyle={selectedStyle}
                onStyleChange={onStyleChange}
              />
            </div>
            
            {/* Right: Clear History Button - Absolute positioned */}
            <div className="absolute right-0">
              <button
                onClick={onClearHistory}
                className="p-2 text-gray-600 dark:text-dark-text-muted hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                title="Clear conversation history"
              >
                <ListX className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Container - scrollable, centered */}
      <div ref={scrollRef} className="flex-1 pt-4 pb-4 overflow-y-auto">
        <div className="flex justify-center min-h-full">
          <div className="w-full max-w-4xl flex flex-col justify-end">
            {sortedHistory.length === 0 && !isProcessing && !isStreaming ? (
              <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-6 py-20">
                <MessageSquare className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
                <p className="text-gray-600 dark:text-dark-text-muted text-lg mb-2">No conversation yet</p>
                <p className="text-gray-500 dark:text-dark-text-muted text-sm max-w-md">
                  Start by typing a prompt below. Your conversation will be saved and you can
                  refer to previous messages using "it", "the button", etc.
                </p>
              </div>
            ) : (
              <>
                {sortedHistory.map((entry, index) => {
                  // Render code_edit entries
                  if (entry.type === 'code_edit' && entry.code_edit_details) {
                    const details = entry.code_edit_details;
                    return (
                      <div key={index} className="py-3 px-6">
                        <CodeEditProgress
                          summary={details.summary}
                          completeSummary={details.complete_summary || details.summary}
                          estimatedLines={details.estimated_lines}
                          currentLines={details.estimated_lines} // Show as complete in history
                          isComplete={details.progress >= 100}
                          isApplied={true} // Historical data is always applied
                          isError={details.isError}
                          isAborted={details.isAborted}
                        />
                      </div>
                    );
                  }
                  
                  // Render message entries
                  if (entry.type === 'message' && entry.message_details) {
                    const details = entry.message_details;
                    return (
                      <ChatMessage
                        key={index}
                        role={details.role}
                        content={details.summary}
                        timestamp={entry.timestamp}
                        model={details.model}
                        isError={details.isError}
                        isWarning={details.isWarning}
                        userImage={currentUser?.image_url}
                      />
                    );
                  }
                  
                  return null;
                })}
                
                {/* Streaming Items (Chat Messages + Code Edits) - Filtered by response_id for seamless handoff */}
                {streamingItems.map((item) => {
                  if (item.type === 'chat') {
                    // Use ChatMessage for warning messages (abort), StreamingChatBubble for normal messages
                    if (item.data.isWarning) {
                      return (
                        <ChatMessage
                          key={`streaming-chat-${item.data.id}`}
                          role="assistant"
                          content={item.data.content}
                          timestamp={item.data.timestamp}
                          isWarning={true}
                          userImage={currentUser?.image_url}
                        />
                      );
                    }
                    return (
                      <StreamingChatBubble
                        key={`streaming-chat-${item.data.id}`}
                        message={item.data.content}
                        model={selectedModel}
                      />
                    );
                  } else {
                    return (
                      <div key={`streaming-code-edit-${item.data.id}`} className="py-3 px-6">
                        <CodeEditProgress
                          summary={item.data.processingMessage}
                          completeSummary={item.data.completeMessage || item.data.processingMessage}
                          estimatedLines={item.data.estimatedLines}
                          currentLines={item.data.currentLines}
                          isComplete={item.data.isComplete}
                          isApplied={item.data.isApplied}
                          isError={item.data.isError}
                          isAborted={item.data.isAborted}
                        />
                      </div>
                    );
                  }
                })}
                
                {/* AI Processing Indicator */}
                {isProcessing && (
                  <div className="py-6 px-6 bg-gray-50 dark:bg-dark-surface">
                    <ProcessingPanel
                      message={processingUpdateMessage || currentProcessingMessage}
                      processingTime={processingTime}
                      modelIcon={currentModelInfo.icon}
                      modelName={currentModelInfo.name}
                      messageKey={processingMessageKey}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

